// The one way an action button's failure is shown: an "Error" popup with the
// message(s) and a single OK. An error means the action stopped — there is
// nothing to retry or continue, so OK only closes it. Used by the list's ⋮
// menu, the open record's action bar, a line editor's actions and the
// confirmation gate once the action itself refuses.

import { useCallback, useState } from 'react'
import type React from 'react'
import { createPortal } from 'react-dom'
import { tw } from '../testHooks'

const overlayStyle: React.CSSProperties = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  // Above the record editor, a line editor and the confirmation gate.
  zIndex: 12100,
}

export default function ActionErrorModal({ messages, onClose, code, fieldLabel }: {
  messages: string[]
  onClose: () => void
  /** A validation's error code (e.g. UVINVSUPP), shown above the description. */
  code?: string | null
  /** The column the error is about. */
  fieldLabel?: string | null
}) {
  if (typeof document === 'undefined') return null // SSR safety
  const list = messages.filter(m => m && m.trim())
  return createPortal(
    <div style={overlayStyle} onClick={onClose}>
      <div
        role="alertdialog" aria-labelledby="twasta-action-error-title"
        onClick={e => e.stopPropagation()}
        {...tw('msg', 'error', { dialog: 'action_error' })}
        onKeyDown={e => { if (e.key === 'Escape' || e.key === 'Enter') { e.preventDefault(); onClose() } }}
        style={{
          background: 'var(--color-surface, #fff)', color: 'var(--color-text, #111)',
          borderRadius: 8, width: 'min(460px, 94vw)',
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
          boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
        }}>
        <div id="twasta-action-error-title" style={{
          padding: '10px 16px', borderBottom: '1px solid var(--color-border, #e5e5e5)',
          fontWeight: 600, fontSize: '0.9625rem', color: 'var(--color-danger, #dc2626)',
        }}>Error</div>
        <div style={{ padding: '14px 16px', fontSize: '0.9225rem', lineHeight: 1.45 }}>
          {code ? (
            <div style={{ marginBottom: 6 }}>
              <span style={{ color: 'var(--color-text-secondary, #666)' }}>Error code: </span>
              <strong>{code}</strong>
            </div>
          ) : null}
          {fieldLabel ? (
            <div style={{ marginBottom: 6 }}>
              <span style={{ color: 'var(--color-text-secondary, #666)' }}>Field: </span>
              <strong>{fieldLabel}</strong>
            </div>
          ) : null}
          {list.length <= 1
            ? (list[0] || 'Action failed')
            : <ul style={{ margin: 0, paddingLeft: 18 }}>{list.map((m, i) => <li key={i}>{m}</li>)}</ul>}
        </div>
        <div style={{
          display: 'flex', justifyContent: 'flex-end',
          padding: '10px 16px', borderTop: '1px solid var(--color-border, #e5e5e5)',
        }}>
          <button
            autoFocus onClick={onClose}
            {...tw('action', 'ok')}
            style={{ padding: '6px 18px', fontSize: '0.8625rem', borderRadius: 6, border: 'none', background: 'var(--color-accent, #4c8dff)', color: '#fff', cursor: 'pointer' }}>
            OK
          </button>
        </div>
      </div>
    </div>,
    document.body,
  )
}

/** ``showActionError(messages)`` opens the popup; render ``actionErrorModal``
 *  once in the component. */
export function useActionError() {
  const [messages, setMessages] = useState<string[] | null>(null)
  const showActionError = useCallback((m: string | string[]) => {
    setMessages(Array.isArray(m) ? m : [m])
  }, [])
  const actionErrorModal = messages
    ? <ActionErrorModal messages={messages} onClose={() => setMessages(null)} />
    : null
  return { showActionError, actionErrorModal }
}

/** The messages a failed action reported: every validation / business-logic
 *  error in the response, else its summary. */
export function actionErrorMessages(
  parsed: { message?: string; validationErrors: Array<{ message?: string }> },
): string[] {
  const msgs = parsed.validationErrors.map(v => v.message || '').filter(Boolean)
  const unique = Array.from(new Set(msgs))
  return unique.length ? unique : [parsed.message || 'Action failed']
}

interface PendingValidationError {
  message: string
  code?: string | null
  fieldLabel?: string | null
  onOk?: () => void
}

/** Imported Vision apps: a failed validation is shown the Vision way — ONE
 *  error at a time (the first), as an Error popup with its code, its
 *  description and a single OK. ``onOk`` runs after the popup has closed
 *  (e.g. to put the cursor on the failing column). */
export function useValidationErrorPopup() {
  const [pending, setPending] = useState<PendingValidationError | null>(null)
  const showValidationError = useCallback((p: PendingValidationError) => {
    setPending(p)
  }, [])
  const validationErrorModal = pending
    ? (
      <ActionErrorModal
        messages={[pending.message]}
        code={pending.code}
        fieldLabel={pending.fieldLabel}
        onClose={() => {
          const cb = pending.onOk
          setPending(null)
          // After the portal unmounts, so focus is not taken back by it.
          if (cb) window.setTimeout(cb, 50)
        }}
      />
    )
    : null
  return { showValidationError, validationErrorModal }
}

/** The error a Vision popup shows: the first blocking one (a warning only
 *  when nothing blocks). */
export function firstValidationError<T extends { severity?: 'E' | 'W' }>(items: T[]): T | null {
  return items.find(i => (i.severity ?? 'E') !== 'W') ?? items[0] ?? null
}
